import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { ButtonGroup, ButtonToolbar } from 'reactstrap';

import Loading from 'src/components/Loading';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import getFullAddressString from 'src/util/address';

import TradeRulePrioritizeNominatedList from './TradeRulePrioritizeNominatedList';

const TradeRulePrioritizeNominatedPropertyFragment = graphql`
  fragment TradeRulePrioritizeNominated_property on Property @throwOnFieldError {
    uuid
    title
    address {
      line1
      line2
      city
      state
      postcode
      gps {
        latitude
        longitude
      }
    }
    ...TradeRulePrioritizeNominatedList_property
  }
`;

const TradeRulePrioritizeNominatedMeterFragment = graphql`
  fragment TradeRulePrioritizeNominated_meter on Meter @throwOnFieldError {
    uuid
    identifier
    title
    tradePointId
    ...TradeRulePrioritizeNominatedList_meter
  }
`;

/**
 * @param {object} props
 * @param {object} props.property
 * @param {object} props.meter
 * @param {object} props.router
 * @returns {React.ReactComponentElement} - TradeRulePrioritizeNominated component
 */
function TradeRulePrioritizeNominated({
  property: propertyRef = null, meter: meterRef = null, router,
}) {
  const intl = useIntl();

  const property = useFragment(TradeRulePrioritizeNominatedPropertyFragment, propertyRef);
  const meter = useFragment(TradeRulePrioritizeNominatedMeterFragment, meterRef);

  if (!property || !meter) {
    return <Loading />;
  }

  const { address, uuid, title: propertyTitle } = property;
  const addr = getFullAddressString(address);
  const meterTitle = meter.title;

  const pageTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.page.title', defaultMessage: 'Enosi - Property {propertyTitle} - Prioritise Trades for {meterTitle}' }, { propertyTitle, meterTitle });

  const allTradeRulesLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.all_trade_rules.label', defaultMessage: 'All trade rules' });
  const propertySettingsLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.property_settings.label', defaultMessage: 'Property Settings' });
  const propertiesBreadcrumbLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.breadcrumbs.properties.label', defaultMessage: 'Properties' });
  const tradeRulesBreadcrumbLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.breadcrumbs.trade_rules.label', defaultMessage: 'Trade Rules' });
  const prioritizeTradeForMeterTitleLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_prioritize_nominated.breadcrumbs.prioritize_trade_for_meter_title.label',
    defaultMessage: 'Prioritise Trades for {meterTitle}',
  }, { meterTitle });
  const manageLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.heading.context.label', defaultMessage: 'Trade Rules - Manage Priority' });

  const propertyControls = (
    <ButtonToolbar>
      <ButtonGroup className="ms-2">
        <Link to={`/properties/${property.uuid}/trade-rules`} className="btn btn-darken">
          {allTradeRulesLabel}
        </Link>
      </ButtonGroup>

      <ButtonGroup className="ms-2">
        <Link to={`/properties/${property.uuid}/settings`} className="btn btn-darken">
          {propertySettingsLabel}
        </Link>
      </ButtonGroup>
    </ButtonToolbar>
  );

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: propertiesBreadcrumbLabel, path: '/properties' },
          { name: propertyTitle, path: `/properties/${uuid}` },
          { name: tradeRulesBreadcrumbLabel, path: `/properties/${uuid}/trade-rules/active` },
          { name: prioritizeTradeForMeterTitleLabel },
        ]}
      />
      <Heading
        title={propertyTitle}
        subtitle={addr}
        context={manageLabel}
        controls={propertyControls}
      />
      <TradeRulePrioritizeNominatedList property={property} meter={meter} router={router} />
    </>
  );
}

TradeRulePrioritizeNominated.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  meter: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  router: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default TradeRulePrioritizeNominated;
